// Everything diplomacy that must not be missed, in one place so the solo game
// and a multiplayer match mount the same four lanes: a treaty offered to you,
// a marriage offered to you, and news of your offers and of war.
//
// The lane and the answer to an offer are one thing (`TreatyDesk`): the
// player answers from here and from the Diplomacy tab, and both answers have
// to reach the same notices — so the desk is owned by whoever mounts the game
// and handed to both.
import type { GameSnapshot } from '@aod/protocol';
import type { PlayerHudEntry } from '@aod/ui';
import { useCallback, useState } from 'react';
import { readDiplomacy } from '../dynasty/diplomacy.js';
import { readDynasty } from '../dynasty/dynasty.js';
import { pendingOffers } from '../dynasty/pendingOffers.js';
import type { TreatyDesk } from '../dynasty/useTreatyNews.js';
import { useOpNews } from '../espionage/useOpNews.js';
import type { DynastyActions } from '../sim/useSimWorld/useDynastyIntents.js';
import { AutoFade } from './AutoFade.js';
import { MarriageProposalToast } from './MarriageProposalToast.js';
import { OpNotices } from './OpNotices.js';
import { TreatyNotices } from './TreatyNotices.js';
import { TreatyOfferToast } from './TreatyOfferToast.js';

export function DiplomacyToasts({
  snapshot,
  players,
  localPlayerIndex,
  desk,
  dynasty: marriage,
  matchOver = false,
}: {
  readonly snapshot: GameSnapshot | null;
  readonly players: readonly PlayerHudEntry[];
  readonly localPlayerIndex: number;
  /** The treaty lane: its notices, and the answer to an offer. */
  readonly desk: TreatyDesk;
  readonly dynasty: DynastyActions;
  /**
   * The match has ended. The sim stops, so an offer that was on the table
   * never lapses — it would sit over the result card for ever. No offer is
   * worth a card now, so none is shown.
   */
  readonly matchOver?: boolean;
}) {
  // Offers whose card has faded. The offer stays on the table (and on the
  // Diplomacy tab); only this card is done. A re-made offer has a new key.
  const [faded, setFaded] = useState<ReadonlySet<string>>(() => new Set());
  const fade = useCallback(
    (key: string) => setFaded((all) => (all.has(key) ? all : new Set(all).add(key))),
    [],
  );
  const diplomacy = readDiplomacy(snapshot);
  const dynasty = readDynasty(snapshot);
  const nameOf = (seat: number) => players.find((p) => p.playerId === seat)?.name ?? `#${seat + 1}`;
  const opNews = useOpNews(snapshot, localPlayerIndex);
  const waiting = matchOver
    ? []
    : pendingOffers(diplomacy, dynasty, null, localPlayerIndex).filter((o) => !faded.has(o.key));
  const treaty = waiting.find((o) => o.kind === 'treaty');
  const wedding = waiting.find((o) => o.kind === 'marriage');
  const offer = treaty?.kind === 'treaty' ? treaty : null;
  const marriageFrom = wedding?.kind === 'marriage' ? wedding.from : null;
  return (
    // One stack in its own lane below the merchant offer: the cards differ in
    // height, so separate fixed lanes would land on one another.
    <div className="pointer-events-none fixed inset-x-0 top-[calc(var(--hud-panel-top)+11rem)] z-50 flex flex-col items-center gap-2 px-3">
      {wedding && (
        <AutoFade key={wedding.key} onGone={() => fade(wedding.key)}>
          <MarriageProposalToast
            dynasty={dynasty}
            from={marriageFrom}
            localSeat={localPlayerIndex}
            fromName={nameOf(marriageFrom ?? 0)}
            onAccept={marriage.accept}
            onDecline={marriage.decline}
          />
        </AutoFade>
      )}
      {offer && (
        <AutoFade key={offer.key} onGone={() => fade(offer.key)}>
          <TreatyOfferToast
            offer={{ ...offer, to: localPlayerIndex }}
            fromName={nameOf(offer.from)}
            onAccept={(from) => {
              desk.answer(from, true);
            }}
            onDecline={(from) => {
              desk.answer(from, false);
            }}
          />
        </AutoFade>
      )}
      {!matchOver && <TreatyNotices notices={desk.notices} nameOf={nameOf} />}
      {!matchOver && <OpNotices entries={opNews} nameOf={nameOf} />}
    </div>
  );
}
